import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import "./style/finalizeStyle.css";
import "./style/style.scss";
import "../../../style/hovers.scss";
import useFinalizeManager from "./hooks/useFinalizeManager";
import FinalizeWithLogin from "./FinalizeWithLogin";
import FinalizeNoLogin from "./FinalizeNoLogin";
import "./style/style.scss";
import BasketFirstStep from "./BasketFirstStep";
import BasketSecondStep from "./BasketSecondStep";
import BasketSurveyStep from "./BasketSurveyStep";
import CheckoutStepper, { CheckoutStep } from "./CheckoutStepper";
import useBasketManager from "./hooks/useBasketManager";
import { useDispatch, useSelector } from "react-redux";
import { State } from "../../store";
import { useTranslation } from "react-i18next";
import { getUserDataByToken } from "../../store/auth/authActions";
import { GET_USER_DATA_URL } from "../../api/apiRoutes";
import useFacility from "../../../utils/hooks/useFacility";
import useParentMessage from "../../../utils/hooks/useParentMessage";
import useBasketQuestions from "./hooks/useBasketQuestions";
import QuestionsModal from "./QuestionsModal";
import { gaEvent } from "../../../utils/Analitics/ga";
import { GA_VIEW_CART } from "../../../utils/Analitics/gaEventsConsts";
import { sendViewCartEvent, convertBasketToViewCartFormat } from "../../../utils/Analitics/GtmEvents";
import { canSendToGA, canSendToGTM } from "../../../utils/Analitics/analiticsConfigByFacility";
import useBasketExtras from "./hooks/useBasketExtras";
import { useBasketSurvey } from "./BasketSurveyStep/hooks/useBasketSurvey";
import {
  CHECKOUT_STEP_KIND_CONFIG,
  CHECKOUT_STEP_KIND_PARAM,
  getCheckoutSubmitTitle,
} from "../../checkout/checkoutStepKindConfig";

function parseInitialFinalizeStep(): number {
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("step");
    if (!raw) {
      return 1;
    }
    const n = parseInt(raw, 10);
    if (Number.isNaN(n) || n < 1 || n > 5) {
      return 1;
    }
    return n;
  } catch {
    return 1;
  }
}

const FinalizePage: React.FC = () => {
  const { scrollToTop } = useParentMessage();
  const {
    getBasket,
    basket,
    deletePromotions,
    total,
    promoCode,
    promoMessage,
    currentPromoCode,
    totalListPrice,
    clearBasket,
  } = useBasketManager();

  const [searchParams, setSearchParams] = useSearchParams();
  const [step, setStep] = useState(parseInitialFinalizeStep);

  const commitStep = useCallback(
    (newStep: number) => {
      setStep(newStep);
      const next = new URLSearchParams(searchParams.toString());
      next.set("step", String(newStep));
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );
  const basketHasSurvey = basket.some((item) => item.hasSurvey);
  const surveyStepActive = step === 2 && basketHasSurvey;

  const { questions, isModalOpen, setIsModalOpen, setAnswer, handleProceed, onSaveAnswers, loading, saveError } =
    useBasketQuestions(basket, getBasket);
  const {
    hasSurvey,
    isLoadingSurvey,
    surveyLoadError,
    isSaving: isSavingSurvey,
    surveySaveError,
    surveyItems,
    currentIndex,
    currentItem,
    currentAnswers,
    setAnswer: setSurveyAnswer,
    goNext,
    goBack,
    invalidQuestionIds,
    firstInvalidQuestionId,
    surveyValidationFailSeq,
    saveSurveyAnswers,
  } = useBasketSurvey(basket, {
    surveyStepActive,
  });
  useBasketExtras();
  const { t } = useTranslation();
  const stepKind = searchParams.get(CHECKOUT_STEP_KIND_PARAM);
  const submitButtonTitle = getCheckoutSubmitTitle(stepKind, total, t);
  const hideExerciseInfo = stepKind !== null && stepKind in CHECKOUT_STEP_KIND_CONFIG;
  const userData = useSelector((state: State) => state.auth.userData);
  const { acceptTransaction, alert, onRemoveItemFromBasket, paymentUrl, loader, saveReservation } =
    useFinalizeManager(clearBasket);
  const isLogging = useSelector((state: State) => state.auth.isLoggedIn);
  const info = useSelector((state: State) => state.alerts.info);
  const basketLength = useSelector((state: State) => state.basket.basketLength);
  const [viewCartEventSent, setViewCartEventSent] = useState(false);
  const dispatch = useDispatch();
  const facilityIds = useFacility();
  const handleSetStep = (nextStep: number) => {
    if (handleProceed()) {
      commitStep(nextStep);
    }
  };

  // Ponowne stosowanie kodu rabatowego po zmianie koszyka
  const reapplyPromoCodeIfNeeded = async () => {
    if (currentPromoCode && currentPromoCode.length > 0) {
      await promoCode(currentPromoCode);
    }
  };

  // Scroll do góry tylko przy wejściu na stronę koszyka (nie przy zmianie ilości/dodawaniu)
  useEffect(() => {
    scrollToTop();
    getBasket();

    if (!userData) {
      dispatch(getUserDataByToken(GET_USER_DATA_URL(facilityIds)));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Odśwież dane gdy info się zmieni (np. komunikaty z API) - bez scrollToTop
  useEffect(() => {
    if (info) {
      getBasket();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info]);

  useEffect(() => {
    getBasket();

    // Jeśli mamy aktywny kod rabatowy, spróbuj go ponownie zastosować
    if (currentPromoCode && currentPromoCode.length > 0) {
      setTimeout(() => reapplyPromoCodeIfNeeded(), 200);
    } else {
      deletePromotions();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basketLength]);

  const handlePromotion = (promotionCode: string) => {
    promoCode(promotionCode);
  };

  const handleDelete = (index: number) => {
    onRemoveItemFromBasket(index);
  };

  const handleGetBasket = () => {
    setTimeout(() => {
      getBasket();
    }, 500);
  };

  useEffect(() => {
    // Fix GA Event structure for ecommerce tracking
    // Calculate total from basket if needed
    const calculatedTotal = basket.reduce((sum, item) => sum + item.price * item.qnt, 0);
    const eventValue = total > 0 ? total : calculatedTotal;
    if (canSendToGA(facilityIds)) {
      gaEvent(
        facilityIds,
        "ecommerce", // Change category to ecommerce
        GA_VIEW_CART,
        {
          event_category: "ecommerce", // Add standard GA4 parameters
          currency: "PLN",
          value: eventValue,
          items: basket.map((item) => ({
            item_id: item.assortIds.replace(/^0,\+/, ""),
            item_name: item.assortName || "Produkt",
            price: item.price,
            quantity: item.qnt,
          })),
        },
        "view_cart", // Add proper label
      );
    }

    //eslint-disable-next-line
  }, []);

  // dodaje parametry z HomePage do URL gdy odświeżymy komponent koszyka (zachowaj ?step= z aktualnego URL)
  useEffect(() => {
    const loadUrlParams = () => {
      const homePageUrl = sessionStorage.getItem("HomePage");
      const stepFromUrl = new URLSearchParams(window.location.search).get("step");

      if (homePageUrl) {
        const homePageParams = homePageUrl.split("?")[1];
        if (homePageParams) {
          const merged = new URLSearchParams(homePageParams);
          if (stepFromUrl) {
            merged.set("step", stepFromUrl);
          }
          const currentUrl = `${window.location.pathname}?${merged.toString()}`;
          window.history.replaceState({}, "", currentUrl);
        }
      }
    };

    loadUrlParams();
  }, []);

  // Event view_cart GTM - wysyłany raz gdy koszyk się załaduje
  useEffect(() => {
    if (basket.length > 0 && !viewCartEventSent && canSendToGTM(facilityIds)) {
      const viewCartData = convertBasketToViewCartFormat(basket, total);
      sendViewCartEvent(facilityIds, viewCartData);
      setViewCartEventSent(true);
    }
  }, [basket, total, viewCartEventSent, facilityIds]);

  // URL z krokiem 2 przy koszyku bez ankiety — po znanym stanie koszyka wróć do kroku 1
  useEffect(() => {
    if (step === 2 && !hasSurvey && basket.length > 0) {
      commitStep(1);
    }
  }, [step, hasSurvey, basket.length, commitStep]);

  const buildSteps = (): CheckoutStep[] => {
    const buyerDataStep = hasSurvey ? 3 : 2;

    const steps: CheckoutStep[] = [
      {
        label: t("podsumowanie_koszyka"),
        onClick: () => commitStep(1),
      },
    ];

    if (hasSurvey) {
      steps.push({
        label: t("dodatkowe_informacje"),
        onClick: step >= 2 ? () => commitStep(2) : undefined,
      });
    }

    steps.push({
      label: t("dane_kupujacego"),
      onClick: step >= buyerDataStep ? () => commitStep(3) : undefined,
    });

    steps.push({
      label: t("platnosc"),
      onClick: undefined,
    });

    return steps;
  };

  const getActiveStepIndex = (): number => {
    if (step === 1) return 0;
    if (step === 2 && hasSurvey) return 1;
    if (step === 3) return hasSurvey ? 2 : 1;
    if (step === 4 || step === 5) return hasSurvey ? 3 : 2;
    return 0;
  };

  return (
    <React.Fragment>
      <div className="basket-container">
        <CheckoutStepper steps={buildSteps()} activeStep={getActiveStepIndex()} />

        {step === 1 && (
          <BasketFirstStep
            promoMessage={promoMessage}
            handlePromotion={handlePromotion}
            handleGetBasket={handleGetBasket}
            handleDelete={handleDelete}
            setStep={handleSetStep}
            basket={basket}
            total={total}
            primaryPrice={totalListPrice}
            hasSurvey={hasSurvey}
            hideExerciseInfo={hideExerciseInfo}
          />
        )}
        {step === 2 && hasSurvey && (
          <BasketSurveyStep
            surveyItems={surveyItems}
            currentIndex={currentIndex}
            currentItem={currentItem}
            currentAnswers={currentAnswers}
            onAnswer={setSurveyAnswer}
            onNext={goNext}
            onBack={goBack}
            onSave={saveSurveyAnswers}
            isSaving={isSavingSurvey}
            surveySaveError={surveySaveError}
            isLoadingSurvey={isLoadingSurvey}
            surveyLoadError={surveyLoadError}
            invalidQuestionIds={invalidQuestionIds}
            firstInvalidQuestionId={firstInvalidQuestionId}
            surveyValidationFailSeq={surveyValidationFailSeq}
            onFinish={() => {
              if (isLogging) {
                handleSetStep(5);
              } else {
                handleSetStep(3);
              }
            }}
            onBasket={() => handleSetStep(1)}
          />
        )}
        {step === 3 && <BasketSecondStep setStep={handleSetStep} />}
        {step === 4 && (
          <FinalizeNoLogin
            alert={alert}
            onPayTransaction={acceptTransaction}
            onSaveReservation={saveReservation}
            total={total}
            paymentUrl={paymentUrl}
            loader={loader}
            basket={basket}
            promoMessage={promoMessage}
            currentPromoCode={currentPromoCode}
            submitButtonTitle={submitButtonTitle}
          />
        )}
        {step === 5 && (
          <FinalizeWithLogin
            alertPayment={alert}
            onPayTransaction={acceptTransaction}
            onSaveReservation={saveReservation}
            total={total}
            paymentUrl={paymentUrl}
            loader={loader}
            basket={basket}
            promoMessage={promoMessage}
            currentPromoCode={currentPromoCode}
            submitButtonTitle={submitButtonTitle}
            setStep={handleSetStep}
          />
        )}
      </div>

      {isModalOpen && (
        <QuestionsModal
          questions={questions}
          onClose={() => setIsModalOpen(false)}
          onAnswer={setAnswer}
          onSaveAnswers={onSaveAnswers}
          loading={loading}
          saveError={saveError}
        />
      )}
    </React.Fragment>
  );
};

export default FinalizePage;
